import React, { createContext, useContext, ReactNode } from 'react';
import { useTenant } from '@/hooks/useTenant';

interface TenantContextValue {
  subdominio: string;
  isCustomDomain: boolean;
  isLocalhost: boolean;
  hostname: string;
  fullUrl: string;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Provider que detecta automaticamente o tenant (imobiliária)
 * baseado no subdomínio ou domínio customizado
 * 
 * Uso:
 * <TenantProvider>
 *   <App />
 * </TenantProvider>
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const tenantInfo = useTenant();

  return (
    <TenantContext.Provider value={tenantInfo}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook para acessar informações do tenant em qualquer componente
 * 
 * @throws Error se usado fora do TenantProvider
 */
export function useTenantContext() {
  const context = useContext(TenantContext);
  
  if (context === undefined) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }
  
  return context;
}

/**
 * HOC para injetar informações do tenant em um componente
 */
export function withTenant<P extends object>(
  Component: React.ComponentType<P & { tenant: TenantContextValue }>
) {
  return function WithTenantComponent(props: P) {
    const tenant = useTenantContext();
    return <Component {...props} tenant={tenant} />;
  };
}
